/**
 * Homepage TikTok video slider — one-card paging + in-place playback.
 */
(function () {
  function initSection(section) {
    if (!section || section.dataset.pbTiktokMounted === '1') return;
    section.dataset.pbTiktokMounted = '1';

    var sectionId = section.getAttribute('data-pb-tiktok-id');
    var track = section.querySelector('[data-pb-tiktok-track]');
    var slides = section.querySelectorAll('[data-pb-tiktok-slide]');
    var prevBtn = section.querySelector('[data-pb-tiktok-prev]');
    var nextBtn = section.querySelector('[data-pb-tiktok-next]');
    var nav = section.querySelector('[data-pb-tiktok-nav]');
    var ac = new AbortController();
    var signal = { signal: ac.signal };

    function tearDown() {
      slides.forEach(function (slide) {
        if (slide._pbTiktokIo) {
          slide._pbTiktokIo.disconnect();
          slide._pbTiktokIo = null;
        }
      });
      ac.abort();
      section.dataset.pbTiktokMounted = '0';
    }

    function onSectionUnload(event) {
      var detail = event.detail;
      if (!detail || detail.sectionId !== sectionId) return;
      document.removeEventListener('shopify:section:unload', onSectionUnload);
      tearDown();
    }
    document.addEventListener('shopify:section:unload', onSectionUnload);

    function scrollStep() {
      var card = track && track.querySelector('[data-pb-tiktok-slide]');
      if (!card) return 280;
      var styles = getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 12;
      return card.offsetWidth + gap;
    }

    function updateArrows() {
      if (!track || !prevBtn || !nextBtn) return;
      var maxScroll = track.scrollWidth - track.clientWidth;
      var canScroll = maxScroll > 2;

      if (nav) {
        if (canScroll) {
          nav.hidden = false;
          nav.removeAttribute('aria-hidden');
        } else {
          nav.hidden = true;
          nav.setAttribute('aria-hidden', 'true');
        }
      }

      prevBtn.disabled = !canScroll || track.scrollLeft <= 1;
      nextBtn.disabled = !canScroll || track.scrollLeft >= maxScroll - 1;
    }

    if (track && prevBtn && nextBtn) {
      prevBtn.addEventListener(
        'click',
        function () {
          track.scrollBy({ left: -scrollStep(), behavior: 'smooth' });
        },
        signal
      );
      nextBtn.addEventListener(
        'click',
        function () {
          track.scrollBy({ left: scrollStep(), behavior: 'smooth' });
        },
        signal
      );
      track.addEventListener('scroll', updateArrows, { passive: true, signal: ac.signal });
      window.addEventListener('resize', updateArrows, signal);
      if (typeof ResizeObserver !== 'undefined') {
        var ro = new ResizeObserver(updateArrows);
        ro.observe(track);
        ac.signal.addEventListener('abort', function () {
          ro.disconnect();
        });
      }
      track.addEventListener(
        'keydown',
        function (event) {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            track.scrollBy({ left: -scrollStep(), behavior: 'smooth' });
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            track.scrollBy({ left: scrollStep(), behavior: 'smooth' });
          }
        },
        signal
      );
      updateArrows();
      requestAnimationFrame(updateArrows);
    }

    function pauseOthers(exceptSlide) {
      slides.forEach(function (slide) {
        if (slide === exceptSlide) return;
        var player = slide.querySelector('[data-pb-tiktok-player]');
        if (!player) return;
        player.pause();
        player.currentTime = 0;
        slide.classList.remove('is-playing');
        var trigger = slide.querySelector('[data-pb-tiktok-trigger]');
        if (trigger) {
          trigger.setAttribute('aria-label', trigger.getAttribute('data-pb-aria-play') || 'Play video');
        }
      });
    }

    slides.forEach(function (slide) {
      var trigger = slide.querySelector('[data-pb-tiktok-trigger]');
      var player = slide.querySelector('[data-pb-tiktok-player]');
      if (!trigger) return;

      var playLabel = trigger.getAttribute('data-pb-aria-play') || 'Play video';
      var pauseLabel = trigger.getAttribute('data-pb-aria-pause') || 'Pause video';

      function syncAria() {
        if (!player) {
          trigger.setAttribute('aria-label', playLabel);
          return;
        }
        if (slide.classList.contains('is-playing') && !player.paused) {
          trigger.setAttribute('aria-label', pauseLabel);
        } else {
          trigger.setAttribute('aria-label', playLabel);
        }
      }

      function play() {
        pauseOthers(slide);
        slide.classList.add('is-playing');
        player.muted = false;
        var promise = player.play();
        if (promise && typeof promise.catch === 'function') {
          promise.catch(function () {
            player.muted = true;
            player.play().catch(function () {});
          });
        }
        syncAria();
      }

      trigger.addEventListener(
        'click',
        function (event) {
          event.preventDefault();
          if (!player) return;
          if (!slide.classList.contains('is-playing')) {
            play();
            return;
          }
          if (player.paused) play();
          else {
            player.pause();
            syncAria();
          }
        },
        signal
      );

      if (!player) return;

      player.addEventListener(
        'click',
        function () {
          if (!slide.classList.contains('is-playing')) return;
          if (player.paused) play();
          else {
            player.pause();
            syncAria();
          }
        },
        signal
      );

      player.addEventListener(
        'ended',
        function () {
          slide.classList.remove('is-playing');
          player.currentTime = 0;
          syncAria();
        },
        signal
      );

      player.addEventListener('play', syncAria, signal);
      player.addEventListener('pause', syncAria, signal);

      if ('IntersectionObserver' in window && track) {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting || !player || player.paused) return;
              player.pause();
              player.currentTime = 0;
              slide.classList.remove('is-playing');
              syncAria();
            });
          },
          { root: track, threshold: 0.4 }
        );
        io.observe(slide);
        slide._pbTiktokIo = io;
      }
    });
  }

  function boot() {
    document.querySelectorAll('[data-pb-tiktok]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('[data-pb-tiktok]');
    if (section) initSection(section);
  });
})();
